import React from 'react';
import { LuMoveUpRight, LuPencil, LuSquare, LuTrash2, LuType, LuUndo2 } from 'react-icons/lu';
import { AccentButton } from '../AccentButton';
import { AccentDialog } from '../../dialogs/AccentDialog';
import { showDialog, t } from '../../services/HelperService';

const COLORS = ['#e11d48', '#2563eb', '#111827'];

const TOOLS = [
  { id: 'rect', Icon: LuSquare, label: () => t('Box') },
  { id: 'arrow', Icon: LuMoveUpRight, label: () => t('Arrow') },
  { id: 'pen', Icon: LuPencil, label: () => t('Pen') },
  { id: 'text', Icon: LuType, label: () => t('Text') },
];

/** Stroke and font sizes that read the same on a small snip and a full-screen capture. */
const metrics = (w, h) => {
  const edge = Math.max(w, h);
  return { line: Math.max(2, Math.round(edge / 300)), font: Math.max(14, Math.round(edge / 40)) };
};

const drawArrow = (ctx, x1, y1, x2, y2, line) => {
  const head = Math.max(10, line * 4);
  const angle = Math.atan2(y2 - y1, x2 - x1);
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - head * Math.cos(angle - Math.PI / 6), y2 - head * Math.sin(angle - Math.PI / 6));
  ctx.lineTo(x2 - head * Math.cos(angle + Math.PI / 6), y2 - head * Math.sin(angle + Math.PI / 6));
  ctx.closePath();
  ctx.fill();
};

const drawShape = (ctx, shape, m) => {
  ctx.save();
  ctx.strokeStyle = shape.color;
  ctx.fillStyle = shape.color;
  ctx.lineWidth = m.line;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  switch (shape.type) {
    case 'rect':
      ctx.strokeRect(Math.min(shape.x1, shape.x2), Math.min(shape.y1, shape.y2), Math.abs(shape.x2 - shape.x1), Math.abs(shape.y2 - shape.y1));
      break;
    case 'arrow':
      drawArrow(ctx, shape.x1, shape.y1, shape.x2, shape.y2, m.line);
      break;
    case 'pen':
      if (shape.points.length > 0) {
        ctx.beginPath();
        ctx.moveTo(shape.points[0].x, shape.points[0].y);
        for (const p of shape.points.slice(1)) ctx.lineTo(p.x, p.y);
        ctx.stroke();
      }
      break;
    case 'text': {
      ctx.font = `600 ${m.font}px sans-serif`;
      ctx.textBaseline = 'top';
      // A white halo keeps the label legible over any background.
      ctx.lineWidth = Math.max(3, m.font / 5);
      ctx.strokeStyle = '#fff';
      ctx.strokeText(shape.text, shape.x, shape.y);
      ctx.fillText(shape.text, shape.x, shape.y);
      break;
    }
    default:
      break;
  }
  ctx.restore();
};

const toolButtonStyle = (active) => ({
  display: 'inline-flex', alignItems: 'center', gap: 4, border: `1px solid ${active ? '#6f42c1' : '#dee2e6'}`, borderRadius: 6,
  background: active ? '#6f42c1' : '#fff', color: active ? '#fff' : '#3b2a6b', fontSize: 12, padding: '3px 8px', cursor: 'pointer', lineHeight: '16px',
});

/**
 * Draw on a screenshot before it goes with the message: a box, an arrow, a pen
 * line or a label, in one of three colours, with undo. Opened over the composer's
 * image chip; resolves { canvas } with the marked-up image (the host sizes it as
 * it does a paste) or { canceled: true }.
 */
export const showImageMarkup = (src) => showDialog(<AccentImageMarkupDlg src={src} />);

const AccentImageMarkupDlg = ({ src, onClose }) => {
  const canvasRef = React.useRef(null);
  const wrapRef = React.useRef(null);
  const [image, setImage] = React.useState(null);
  const [shapes, setShapes] = React.useState([]);
  const [drawing, setDrawing] = React.useState(null);
  const [tool, setTool] = React.useState('rect');
  const [color, setColor] = React.useState(COLORS[0]);
  const [textEdit, setTextEdit] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const img = new Image();
    img.onload = () => setImage(img);
    img.onerror = () => setError(t('The image could not be loaded.'));
    img.src = src;
  }, [src]);

  const m = React.useMemo(() => (image ? metrics(image.naturalWidth, image.naturalHeight) : { line: 2, font: 14 }), [image]);

  // Every change repaints the whole picture: the image, the committed shapes, the one in progress.
  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !image) return;
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, 0, 0);
    for (const s of shapes) drawShape(ctx, s, m);
    if (drawing) drawShape(ctx, drawing, m);
  }, [image, shapes, drawing, m]);

  // Pointer position in image pixels (the canvas is shown scaled to fit).
  const pointOf = (e) => {
    const canvas = canvasRef.current;
    const b = canvas.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(canvas.width, (e.clientX - b.left) * (canvas.width / b.width))),
      y: Math.max(0, Math.min(canvas.height, (e.clientY - b.top) * (canvas.height / b.height))),
    };
  };
  // The same point in the wrapper's coordinates, for the text box.
  const screenPointOf = (p) => {
    const canvas = canvasRef.current;
    const b = canvas.getBoundingClientRect();
    const w = wrapRef.current.getBoundingClientRect();
    return { left: b.left - w.left + p.x * (b.width / canvas.width), top: b.top - w.top + p.y * (b.height / canvas.height), scale: b.width / canvas.width };
  };

  const commitText = () => {
    const edit = textEdit;
    setTextEdit(null);
    if (!edit) return;
    const text = (edit.value ?? '').trim();
    if (text) setShapes((prev) => [...prev, { type: 'text', x: edit.x, y: edit.y, text, color }]);
  };

  const onPointerDown = (e) => {
    if (!image || e.button !== 0) return;
    e.preventDefault();
    if (textEdit) { commitText(); return; }
    const p = pointOf(e);
    if (tool === 'text') {
      setTextEdit({ x: p.x, y: p.y, value: '' });
      return;
    }
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDrawing(tool === 'pen' ? { type: 'pen', points: [p], color } : { type: tool, x1: p.x, y1: p.y, x2: p.x, y2: p.y, color });
  };
  const onPointerMove = (e) => {
    if (!drawing) return;
    const p = pointOf(e);
    setDrawing((d) => (d.type === 'pen' ? { ...d, points: [...d.points, p] } : { ...d, x2: p.x, y2: p.y }));
  };
  const onPointerUp = () => {
    if (!drawing) return;
    const d = drawing;
    setDrawing(null);
    const moved = d.type === 'pen' ? d.points.length > 1 : (Math.abs(d.x2 - d.x1) > 2 || Math.abs(d.y2 - d.y1) > 2);
    if (moved) setShapes((prev) => [...prev, d]);
  };

  const undo = () => { setTextEdit(null); setShapes((prev) => prev.slice(0, -1)); };
  const clear = () => { setTextEdit(null); setShapes([]); };

  const use = (e) => {
    if (textEdit) commitText();
    const canvas = canvasRef.current;
    if (!canvas || !image) { onClose({ canceled: true }); e.promise(); return; }
    // The in-progress text (if any) was just committed; repaint once more so it is on the pixels handed back.
    const out = document.createElement('canvas');
    out.width = canvas.width;
    out.height = canvas.height;
    const ctx = out.getContext('2d');
    ctx.drawImage(image, 0, 0);
    const all = textEdit && (textEdit.value ?? '').trim() ? [...shapes, { type: 'text', x: textEdit.x, y: textEdit.y, text: textEdit.value.trim(), color }] : shapes;
    for (const s of all) drawShape(ctx, s, m);
    onClose({ canvas: out, marked: all.length > 0 });
    e.promise();
  };

  const textBox = textEdit && canvasRef.current && wrapRef.current ? screenPointOf(textEdit) : null;

  return (
    <AccentDialog
      headerContent={t('Mark up the screenshot')}
      onClose={onClose}
      relativeWidth={(d) => d.width - 120}
      relativeHeight={(d) => d.height - 120}
      actionsContent={[
        <AccentButton key="cancel" onClick={(e) => { onClose({ canceled: true }); e.promise(); }}>{t('Cancel')}</AccentButton>,
        <AccentButton key="use" onClick={use} disabled={!image}>{t('Use image')}</AccentButton>,
      ]}
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }} data-tagname="markupTools">
          {TOOLS.map(({ id, Icon, label }) => (
            <button key={id} type="button" data-tagname={`markupTool_${id}`} style={toolButtonStyle(tool === id)} onClick={() => { setTextEdit(null); setTool(id); }}>
              <Icon size={13} />
              <span>{label()}</span>
            </button>
          ))}
          <span style={{ width: 8 }} />
          {COLORS.map((c) => (
            <button
              key={c}
              type="button"
              title={t('Colour')}
              onClick={() => setColor(c)}
              style={{ width: 20, height: 20, borderRadius: 10, border: color === c ? '2px solid #6f42c1' : '2px solid #fff', boxShadow: '0 0 0 1px #dee2e6', background: c, cursor: 'pointer', padding: 0 }}
            />
          ))}
          <span style={{ flex: 1 }} />
          <button type="button" style={toolButtonStyle(false)} disabled={shapes.length === 0} onClick={undo} title={t('Undo')}>
            <LuUndo2 size={13} />
            <span>{t('Undo')}</span>
          </button>
          <button type="button" style={toolButtonStyle(false)} disabled={shapes.length === 0} onClick={clear} title={t('Remove every mark')}>
            <LuTrash2 size={13} />
            <span>{t('Clear')}</span>
          </button>
        </div>
        <small className="text-muted">{t('Draw a box or an arrow around what matters, or add a label; the marked-up picture goes with your message.')}</small>
        {error && <small className="text-danger">{error}</small>}
        <div
          ref={wrapRef}
          style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f1f3f5', border: '1px solid #dee2e6', borderRadius: 6, overflow: 'hidden' }}
        >
          <canvas
            ref={canvasRef}
            data-tagname="markupCanvas"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            style={{ maxWidth: '100%', maxHeight: '100%', display: image ? 'block' : 'none', cursor: tool === 'text' ? 'text' : 'crosshair', touchAction: 'none', background: '#fff' }}
          />
          {!image && !error && <small className="text-muted">{t('Loading…')}</small>}
          {textBox && (
            <input
              autoFocus
              data-tagname="markupTextInput"
              value={textEdit.value}
              placeholder={t('Label…')}
              onChange={(e) => setTextEdit((prev) => ({ ...prev, value: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); commitText(); }
                if (e.key === 'Escape') { e.preventDefault(); setTextEdit(null); }
              }}
              onBlur={commitText}
              style={{
                position: 'absolute', left: textBox.left, top: textBox.top, minWidth: 120,
                font: `600 ${Math.max(12, m.font * textBox.scale)}px sans-serif`, color,
                border: `1px dashed ${color}`, background: 'rgba(255,255,255,0.9)', padding: '0 4px', outline: 'none',
              }}
            />
          )}
        </div>
      </div>
    </AccentDialog>
  );
};
